import React, { useState, useEffect, useRef } from 'react';
import socket from "../../socket";
import { Box, Typography, Avatar, IconButton, Fab, Tooltip, Divider, Chip } from '@mui/material';
import {
    CallEnd as CallEndIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Videocam as VideocamIcon,
    VideocamOff as VideocamOffIcon,
    ScreenShare as ScreenShareIcon,
    StopScreenShare as StopScreenShareIcon,
    Fullscreen as FullscreenIcon
} from '@mui/icons-material';

const CallWindow = ({ activeChannel, callType, onHangUp, currentUser, isCaller }) => {
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(callType === 'audio');
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [duration, setDuration] = useState(0);
    
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const localStreamRef = useRef(null);
    const screenStreamRef = useRef(null);
    const peerConnection = useRef(null);

    const configuration = {
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" }
      ]
    };

    useEffect(() => {
        const timer = setInterval(() => {
            setDuration(prev => prev + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const [mediaReady, setMediaReady] = useState(false);
    const peerAccepted = useRef(false);
    const pendingOffer = useRef(null);

    useEffect(() => {
        let isCancelled = false;
        let iceCandidateQueue = [];

        // 1. Initialize Peer Connection
        const pc = new RTCPeerConnection(configuration);
        peerConnection.current = pc;

        pc.addTransceiver('audio', { direction: 'sendrecv' });
        if (callType === 'video') pc.addTransceiver('video', { direction: 'sendrecv' });

        const processIceQueue = async () => {
            while (iceCandidateQueue.length > 0) {
                const candidate = iceCandidateQueue.shift();
                try {
                    await pc.addIceCandidate(new RTCIceCandidate(candidate));
                } catch(e) {
                    console.error("Error adding queued ICE Candidate:", e);
                }
            }
        };

        const createAndSendOffer = async () => {
            if (pc.signalingState !== "stable") return;
            console.log("Creating and sending offer...");
            try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                socket.emit("webrtc-offer", { channel: activeChannel.id, offer: offer, from: currentUser.id });
            } catch (err) {
                console.error("Error creating offer:", err);
            }
        };

        const handleOffer = async (offerData) => {
            if (pc.signalingState !== "stable") return;
            console.log("Handling offer from", offerData.from);
            try {
                await pc.setRemoteDescription(new RTCSessionDescription(offerData.offer));
                await processIceQueue();
                
                // Ensure tracks are added before creating answer
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                socket.emit("webrtc-answer", { channel: activeChannel.id, answer: answer, from: currentUser.id });
            } catch (err) {
                console.error("Error handling offer:", err);
            }
        };

        // 2. Setup track handling
        pc.ontrack = (event) => {
            console.log("Received remote track:", event.track.kind);
            const remoteStream = event.streams[0] || new MediaStream([event.track]);
            
            if (event.track.kind === 'video') {
                if (remoteVideoRef.current) {
                    remoteVideoRef.current.srcObject = remoteStream;
                    remoteVideoRef.current.play().catch(console.error);
                }
            } else {
                if (remoteAudioRef.current) {
                    remoteAudioRef.current.srcObject = remoteStream;
                    remoteAudioRef.current.play().catch(console.error);
                }
            }
        };

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                socket.emit("webrtc-ice-candidate", {
                    channel: activeChannel.id,
                    candidate: event.candidate,
                    from: currentUser.id
                });
            }
        };

        // 3. Signaling Listeners
        socket.on("call-response", (data) => {
            if (isCaller && data.accepted && data.channel === activeChannel.id) {
                console.log("Peer accepted. Checking media readiness...");
                peerAccepted.current = true;
                if (localStreamRef.current) {
                    createAndSendOffer();
                }
            }
        });

        socket.on("webrtc-offer", async (data) => {
            if (data.from === currentUser.id) return;
            if (localStreamRef.current) {
                handleOffer(data);
            } else {
                console.log("Media not ready, queuing offer...");
                pendingOffer.current = data;
            }
        });

        socket.on("webrtc-answer", async (data) => {
            if (data.from === currentUser.id || pc.signalingState === "stable") return;
            console.log("Handling answer from", data.from);
            try {
                await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
                await processIceQueue();
            } catch (err) {
                console.error("Error handling answer:", err);
            }
        });

        socket.on("webrtc-ice-candidate", async (data) => {
            if (data.from === currentUser.id) return;
            if (pc.remoteDescription) {
                await pc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(console.error);
            } else {
                iceCandidateQueue.push(data.candidate);
            }
        });

        socket.on("end-call", (data) => {
            if (data.channel === activeChannel.id) handleLocalHangup();
        });

        // 4. Init Media
        const initMedia = async () => {
            try {
                console.log("Initializing local media...");
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: callType === "video"
                });

                if (isCancelled) {
                    stream.getTracks().forEach(t => t.stop());
                    return;
                }

                localStreamRef.current = stream;
                if (localVideoRef.current) localVideoRef.current.srcObject = stream;

                // Add tracks to PC
                stream.getTracks().forEach(track => pc.addTrack(track, stream));
                setMediaReady(true);

                // If we are caller and peer already accepted, send offer now.
                if (isCaller && peerAccepted.current) {
                    createAndSendOffer();
                }
                
                // If we are receiver and already have a pending offer, answer now.
                if (!isCaller && pendingOffer.current) {
                    handleOffer(pendingOffer.current);
                    pendingOffer.current = null;
                }
                
                // Final fallback for caller
                if (isCaller) {
                    setTimeout(() => {
                        if (pc.signalingState === "stable" && !peerAccepted.current) {
                            console.log("Fallback: sending offer anyway...");
                            createAndSendOffer();
                        }
                    }, 8000);
                }

            } catch (err) {
                console.error("Media Error:", err);
                alert("Please check camera/mic permissions.");
            }
        };

        initMedia();

        return () => {
            isCancelled = true;
            if (peerConnection.current) peerConnection.current.close();
            if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
            socket.off("webrtc-offer");
            socket.off("webrtc-answer");
            socket.off("webrtc-ice-candidate");
            socket.off("call-response");
            socket.off("end-call");
        };
    }, []);

    // Toggle Mute
    useEffect(() => {
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach(track => {
                track.enabled = !isMuted;
            });
        }
    }, [isMuted]);

    // Toggle Video
    useEffect(() => {
        if (localStreamRef.current) {
            localStreamRef.current.getVideoTracks().forEach(track => {
                track.enabled = !isVideoOff;
            });
        }
    }, [isVideoOff]);

    const stopScreenShare = () => {
        if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(t => t.stop());
            screenStreamRef.current = null;
        }
        if (peerConnection.current && localStreamRef.current) {
            const videoTrack = localStreamRef.current.getVideoTracks()[0];
            const senders = peerConnection.current.getSenders();
            const videoSender = senders.find(s => s.track && s.track.kind === 'video');
            if (videoSender && videoTrack) {
                videoSender.replaceTrack(videoTrack).catch(console.error);
            }
        }
        if (localVideoRef.current && localStreamRef.current) {
            localVideoRef.current.srcObject = localStreamRef.current;
        }
        setIsScreenSharing(false);
    };

    const toggleScreenShare = async () => {
        if (isScreenSharing) {
            stopScreenShare();
            return;
        }

        try {
            if (!navigator.mediaDevices?.getDisplayMedia) {
                alert("Screen sharing is not supported by this browser.");
                return;
            }

            const screenStream = await navigator.mediaDevices.getDisplayMedia({
                video: { cursor: "always" },
                audio: false
            });

            screenStreamRef.current = screenStream;
            const screenTrack = screenStream.getVideoTracks()[0];

            screenTrack.onended = () => {
                stopScreenShare();
            };

            if (peerConnection.current) {
                const senders = peerConnection.current.getSenders();
                const videoSender = senders.find(s => s.track && s.track.kind === 'video');
                if (videoSender) {
                    await videoSender.replaceTrack(screenTrack);
                } else if (localStreamRef.current) {
                    peerConnection.current.addTrack(screenTrack, screenStream);
                }
            }

            if (localVideoRef.current) {
                localVideoRef.current.srcObject = screenStream;
            }

            setIsScreenSharing(true);
        } catch (err) {
            console.warn("Screen share cancelled or failed:", err);
        }
    };

    const handleLocalHangup = () => {
        stopScreenShare();
        if (peerConnection.current) {
            peerConnection.current.close();
            peerConnection.current = null;
        }
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => track.stop());
            localStreamRef.current = null;
        }
        onHangUp();
    };    

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: '#1a1a1a',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            overflow: 'hidden'
        }}>
            {/* Background/Video Feed Simulation */}
            <Box sx={{
                flex: 1,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Box sx={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, #1f1f1f 0%, #4b53bc 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}>
                    <video 
                      ref={remoteVideoRef} 
                      autoPlay 
                      playsInline 
                      style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover', 
                          display: callType === "video" ? 'block' : 'none' 
                      }} 
                    />
                    
                    {(!isVideoOff && callType !== 'video') && (
                        <Avatar
                            sx={{
                                width: 180,
                                height: 180,
                                bgcolor: '#4b53bc',
                                color: '#ffffff',
                                fontSize: 64,
                                fontWeight: 700,
                                position: 'absolute',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
                            }}
                        >
                            {activeChannel?.name?.charAt(0).toUpperCase() || '?'}
                        </Avatar>
                    )}
                </Box>

                {/* Small Self View Overlay / Local Video */}
                {(!isVideoOff || callType === "video") && (
                    <Box sx={{
                        position: 'absolute',
                        bottom: 110,
                        right: 24,
                        width: 240,
                        height: 160,
                        bgcolor: '#000',
                        borderRadius: 2,
                        border: '2px solid rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        zIndex: 2,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.5)'
                    }}>
                        <video 
                          ref={localVideoRef} 
                          autoPlay 
                          playsInline 
                          muted 
                          style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover',
                              transform: isScreenSharing ? 'none' : 'scaleX(-1)'
                          }} 
                        />
                        <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: 'rgba(0,0,0,0.6)', px: 1, py: 0.2, borderRadius: 1, color: '#fff', fontSize: '11px' }}>
                           {currentUser?.name || 'User'} (You){isScreenSharing ? ' • Sharing Screen' : ''}
                        </Typography>
                    </Box>
                )}

                {/* Call Info Overlay */}
                <Box sx={{
                    position: 'absolute',
                    top: 40,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    textShadow: '0px 2px 4px rgba(0,0,0,0.5)',
                    zIndex: 2
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#ffffff' }}>{activeChannel?.name || 'Live Call'}</Typography>
                    <Typography variant="body1" sx={{ opacity: 0.85, color: '#ffffff', mt: 0.5 }}>{formatDuration(duration)}</Typography>
                    {isScreenSharing && (
                        <Chip
                            label="Your Screen is being shared"
                            size="small"
                            sx={{
                                mt: 1.5,
                                bgcolor: '#4b53bc',
                                color: '#ffffff',
                                fontWeight: 600,
                                fontSize: '11px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* Control Bar */}
            <Box sx={{
                height: 100,
                bgcolor: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(20px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
                <Tooltip title={isMuted ? "Unmute Microphone" : "Mute Microphone"}>
                    <Fab
                        size="medium"
                        onClick={() => setIsMuted(!isMuted)}
                        sx={{
                            bgcolor: isMuted ? '#f44336' : 'rgba(255,255,255,0.1)',
                            color: '#fff',
                            '&:hover': { bgcolor: isMuted ? '#d32f2f' : 'rgba(255,255,255,0.2)' }
                        }}
                    >
                        {isMuted ? <MicOffIcon /> : <MicIcon />}
                    </Fab>
                </Tooltip>

                <Tooltip title={isVideoOff ? "Turn Camera On" : "Turn Camera Off"}>
                    <Fab
                        size="medium"
                        onClick={() => setIsVideoOff(!isVideoOff)}
                        sx={{
                            bgcolor: isVideoOff ? '#f44336' : 'rgba(255,255,255,0.1)',
                            color: '#fff',
                            '&:hover': { bgcolor: isVideoOff ? '#d32f2f' : 'rgba(255,255,255,0.2)' }
                        }}
                    >
                        {isVideoOff ? <VideocamOffIcon /> : <VideocamIcon />}
                    </Fab>
                </Tooltip>

                <Tooltip title={isScreenSharing ? "Stop Sharing Screen" : "Share Screen"}>
                    <Fab
                        size="medium"
                        onClick={toggleScreenShare}
                        sx={{
                            bgcolor: isScreenSharing ? '#4b53bc' : 'rgba(255,255,255,0.1)',
                            color: '#fff',
                            '&:hover': {
                                bgcolor: isScreenSharing ? '#3b43a2' : 'rgba(255,255,255,0.2)'
                            }
                        }}
                    >
                        {isScreenSharing ? <StopScreenShareIcon /> : <ScreenShareIcon />}
                    </Fab>
                </Tooltip>

                <Tooltip title="End Call">
                    <Fab
                        color="error"
                        onClick={() => {
                           socket.emit("end-call", { channel: activeChannel.id });
                           handleLocalHangup();
                        }}
                        sx={{
                            width: 64,
                            height: 64,
                            bgcolor: '#f44336',
                            '&:hover': { bgcolor: '#d32f2f' }
                        }}
                    >
                        <CallEndIcon fontSize="large" />
                    </Fab>
                </Tooltip>
            </Box>
            <audio ref={remoteAudioRef} autoPlay />
        </Box>
    );
};

export default CallWindow;
